import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
	Controller,
	type ControllerProps,
	type FieldError,
	type FieldPath,
	type FieldValues,
	FormProvider,
	useFormContext,
	useFormState
} from 'react-hook-form';

import { Label } from './label';
import { cn } from '@sunfish/lib/utils';

const Form = FormProvider;

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
	name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	...props
}: ControllerProps<TFieldValues, TName>) => {
	return (
		<FormFieldContext.Provider value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext.Provider>
	);
};

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState, formState } = useFormContext();

	const fieldState = getFieldState(fieldContext.name, formState);

	if (!fieldContext) {
		throw new Error('useFormField should be used within <FormField>');
	}

	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState
	};
};

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		const id = React.useId();

		return (
			<FormItemContext.Provider value={{ id }}>
				<div ref={ref} className={cn('space-y-1', className)} {...props} />
			</FormItemContext.Provider>
		);
	}
);
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
	React.ElementRef<typeof LabelPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
	const { error, formItemId } = useFormField();
	return <Label ref={ref} className={cn(error && 'text-destructive', className)} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
	({ ...props }, ref) => {
		const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

		return (
			<Slot
				ref={ref}
				id={formItemId}
				aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
				aria-invalid={!!error}
				{...props}
			/>
		);
	}
);
FormControl.displayName = 'FormControl';

// A div, not a p: rich-text descriptions render block markup that is invalid
// inside a paragraph and triggers React hydration errors.
const FormDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, children, ...props }, ref) => {
		const { formDescriptionId } = useFormField();

		// Blocks render this unconditionally, so without this guard a field with no
		// description still contributes an empty row to FormItem's `space-y`, which
		// reads as a stray gap between the label and its input.
		if (children === null || children === undefined || children === '') {
			return null;
		}

		return (
			<div
				ref={ref}
				id={formDescriptionId}
				// Rich descriptions render a nested `prose prose-sm` block whose own
				// font-size would otherwise beat the text-xs set here, so the scale is
				// restated for that descendant rather than left to class ordering.
				className={cn(
					'text-xs leading-snug text-muted-foreground [&_.prose]:text-xs [&_.prose]:leading-snug',
					className
				)}
				{...props}
			>
				{children}
			</div>
		);
	}
);
FormDescription.displayName = 'FormDescription';

/**
 * Extracts an error message from a FieldError or nested error object.
 * Handles cases where Zod validation returns nested errors for object schemas.
 */
const getErrorMessage = (error: FieldError | Record<string, unknown> | undefined): string | undefined => {
	if (!error) return undefined;

	// Direct message property (standard FieldError)
	if (typeof error.message === 'string') {
		return error.message;
	}

	// For nested errors (e.g., from z.object()), collect all nested messages
	const nestedMessages: string[] = [];
	for (const value of Object.values(error)) {
		if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
			nestedMessages.push(value.message);
		}
	}

	if (nestedMessages.length > 0) {
		return nestedMessages.join(', ');
	}

	return undefined;
};

const FormMessage = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, children, ...props }, ref) => {
		const { error, formMessageId } = useFormField();
		const body = error ? getErrorMessage(error as FieldError | Record<string, unknown>) : children;

		if (!body) {
			return null;
		}

		return (
			<p
				ref={ref}
				id={formMessageId}
				className={cn('text-[0.8rem] font-medium text-destructive', className)}
				{...props}
			>
				{body}
			</p>
		);
	}
);
FormMessage.displayName = 'FormMessage';

const FormRootError = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => {
		const { errors } = useFormState();
		const rootError = errors.root;
		if (!rootError) {
			return null;
		}
		return (
			<div
				ref={ref}
				className={cn(
					'bg-destructive border-destructive text-destructive-foreground text-sm font-medium p-2 rounded-lg',
					className
				)}
				{...props}
			>
				{rootError.message}
			</div>
		);
	}
);
FormRootError.displayName = 'FormRootError';

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, FormRootError, useFormField };
